/**
 * Description: This is yet another Tooltip class for MooTools 1.3. See
 *    README.md for details
 * License: MIT-style
 * Authors:
 *    - Korney Czukowski
 * Requires:
 *    - core/1.3: [Class, Options, Events, Element, Element.Event]
 *    - more/1.3: [Element.Position, Element.Shortcuts]
 * Provides: [Tooltip]
 *
 * Modified by SCT for iFixit.
 */

// eslint-disable-next-line no-var
export var Tooltip = (window.Tooltip = new Class({
   Implements: [Events, Options],
   Binds: ['hide'],

   options: {
      autoshow: false,
      showDelay: 200,
      autohide: true,
      hideDelay: 200,
      offset: 0,
      nudge: 0,
      arrowInset: 0,
      side: 'bottom',
      arrowAlign: 'center',
      zIndex: undefined,
      maxWidth: undefined,
      maxHeight: undefined,
      activate: null,
      text: null,
   },

   showTimer: null,
   hideTimer: null,
   isPinned: false,

   initialize: function (el, options) {
      this.setOptions(options);
      this.setAnchor(el);
   },

   create: function () {
      this.created = true;
      this.tooltip = Tooltip.container({
         style: {
            zIndex: this.options.zIndex,
            maxWidth: this.options.maxWidth,
            maxHeight: this.options.maxHeight,
         },
      });

      this.arrow = Tooltip.arrow({
         side: this.options.side,
         align: this.options.arrowAlign,
         zIndex: this.options.zIndex,
      });

      this._setupAutoHide(this.tooltip);
      this.set(this.options.text);
   },

   /**
    * Setup the event handlers necessary for auto-hiding the tooltip on
    * mouse-out
    */
   _setupAutoHide: function (element) {
      if (!element) {
         return;
      }
      let self = this;
      let events = {
         mouseenter: function () {
            if (self.options.autohide) {
               window.clearTimeout(self.hideTimer);
            }
         },
         mouseleave: function () {
            if (self.options.autohide) {
               self.hideTimer = self.hide.delay(self.options.hideDelay, self);
            }
         },
      };
      if (element.isVisible()) {
         const resize = function () {
            self.position(element);
            self.arrow.pin();
            self.tooltip.pin();
         };
         window.addEvent('resize', resize);
         element.store('resize', resize);
      }
      window.clearTimeout(self.hideTimer);
      element.addEvents(events);
      element.store('tooltip-events', events);
   },

   _teardownAutoHide: function (element) {
      let events = element.retrieve('tooltip-events');
      if (events) {
         element.removeEvents(events);
      }
      window.removeEvent('resize', element.retrieve('resize'));
   },

   /**
    * Set the element that this tooltip should be anchored to (shown for)
    */
   setAnchor: function (newAnchorElement) {
      this.element = newAnchorElement;

      if (this.options.autoshow) {
         let showTimer;
         const self = this;
         newAnchorElement.addEvents({
            mouseenter: function () {
               showTimer = self.show.delay(self.options.showDelay, self);
            },
            mouseleave: function () {
               window.clearTimeout(showTimer);
            },
         });
      }
   },

   set: function (content) {
      if (!this.created) {
         this.create();
      }
      let container = this.tooltip.getElement('.content');

      if (typeOf(content) == 'element') {
         container.empty().grab(content);
      } else {
         container.set('html', content);
      }

      // Set the widths and heights of the horizontal and vertical edges using
      // the dimensions of the content minus ten pixels for the space that the
      // corners take up. Make sure that the height and width don't go
      // negative, which can happen in IE for a single line of text; IE will
      // throw an error when you try to use setStyle to set a negative
      // width/height.

      this.tooltip.makeInvisible().show();
      let dims = this.tooltip.getSize();
      this.tooltip.hide().makeVisible();

      let y = Math.max(0, dims.y - 10);
      this.tooltip.getElement('.el').setStyle('height', y);
      this.tooltip.getElement('.er').setStyle('height', y);

      let x = Math.max(0, dims.x - 10);
      this.tooltip.getElement('.et').setStyle('width', x);
      this.tooltip.getElement('.eb').setStyle('width', x);

      return this;
   },

   show: function () {
      if (!this.created) {
         this.create();
      }
      if (this.isVisible) {
         this.hide();
      }
      if (this.element) {
         this._setupAutoHide(this.element);
      }
      this.isVisible = true;
      this.tooltip.show();
      this.arrow.show();
      this.position(this.element);
      if (this.element.isFixed()) {
         this.arrow.pin();
         this.tooltip.pin();
         this.isPinned = true;
      }
      this.fireEvent('show');
      return this;
   },

   hide: function () {
      window.clearTimeout(this.hideTimer);
      if (!this.isVisible) {
         return this;
      }
      if (this.element) {
         this._teardownAutoHide(this.element);
      }
      if (this.isPinned) {
         this.arrow.unpin();
         this.tooltip.unpin();
      }
      this.tooltip.hide();
      this.arrow.hide();
      this.fireEvent('hide');
      this.isVisible = false;
      return this;
   },

   toggle: function () {
      if (this.visible()) {
         this.hide();
      } else {
         this.show();
      }
   },

   visible: function () {
      return this.isVisible;
   },

   position: function (anchorElement) {
      let tip = {
         bottom: { edge: 'top', sign: 1, dim: 'y' },
         top: { edge: 'bottom', sign: -1, dim: 'y' },
         left: { edge: 'right', sign: -1, dim: 'x' },
         right: { edge: 'left', sign: 1, dim: 'x' },
      }[this.options.side];

      let positionAlign = this.options.arrowAlign;

      let arrowAlign =
         tip.dim == 'y' &&
         anchorElement.getPosition().x - document.body.scrollLeft + this.tooltip.getSize().x >
            window.innerWidth
            ? 'right'
            : this.options.arrowAlign;

      // Use getStyle, because the arrow is presently display:none, making
      // getSize useless.
      let arrowSize = {
         x: this.arrow.getStyle('width').toInt(),
         y: this.arrow.getStyle('height').toInt(),
      };

      // Optionally inset the arrow farther than the default.
      let arrowMargin = 0;
      if (/^(top|bottom|left|right)$/.test(arrowAlign)) {
         let marginAttr = 'margin-' + arrowAlign;
         arrowMargin = this.arrow.getStyle(marginAttr);
         if (arrowMargin) {
            arrowMargin = arrowMargin.toInt();
            this.arrow.setStyle(marginAttr, this.options.arrowInset);
            arrowMargin = this.options.arrowInset;
         }
      }

      // If the arrow is aligned to one side (anything but middle), shift the
      // entire tooltip to adjust for the arrow's inset from the edge of the
      // tooltip, so that the point of the arrow aligns exactly with the
      // content.
      let offset, adjust, sign;
      if (['top', 'bottom'].contains(arrowAlign)) {
         adjust = arrowSize.y / 2;
         adjust += arrowMargin;
         sign = arrowAlign == 'top' ? -1 : 1;
         offset = { x: 0, y: adjust * sign };
      } else if (['left', 'right'].contains(arrowAlign)) {
         adjust = arrowSize.x / 2;
         adjust += arrowMargin;
         sign = arrowAlign == 'left' ? -1 : 1;
         offset = { x: adjust * sign, y: 0 };
      } else {
         offset = { x: 0, y: 0 };
      }

      // Distance the tooltip from the element, taking into account the extra
      // gap needed to account for the arrow itself.
      offset[tip.dim] = (this.options.offset + arrowSize[tip.dim]) * tip.sign;

      // Nudge the tooltip along the axis perpendicular to the tooltip's side
      // (e.g. if side is 'top', then nudge moves the tooltip left/right).
      offset[tip.dim == 'x' ? 'y' : 'x'] += this.options.nudge;

      this.tooltip.position({
         relativeTo: anchorElement,
         // Disable this for now until we figure out why it breaks positioning
         relFixedPosition: false && anchorElement.isFixed(),
         edge: tip.edge + arrowAlign,
         position: this.options.side + positionAlign,
         offset: offset,
      });

      let arrowSide = this.options.side;
      this.arrow.position({
         relativeTo: this.tooltip,
         position: this.arrow.retrieve('Tooltip:side') + arrowAlign,
         edge: arrowSide + arrowAlign,
      });

      return this;
   },
}));

Tooltip.extend({
   corners: ['ctl', 'ctr', 'cbr', 'cbl'],
   edges: ['et', 'er', 'eb', 'el'],

   arrowPositionMap: {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
   },

   container: function (options) {
      let tooltip = new Element('div.tooltip').hide();
      new Element('div.content').inject(tooltip);

      Tooltip.edges.concat(Tooltip.corners).each(function (className) {
         new Element('div.' + className).inject(tooltip);
      });

      tooltip.setStyles(options.style);
      tooltip.inject(document.body);

      return tooltip;
   },

   arrow: function (options) {
      let mappedSide = this.arrowPositionMap[options.side];
      let className = '.tooltip-a' + mappedSide.charAt(0);

      let el = new Element('div' + className);
      el.store('Tooltip:side', mappedSide);
      el.hide().inject(document.body);

      if (options.zIndex) {
         el.setStyle('zIndex', options.zIndex);
      }

      return el;
   },

   /**
    * Tooltip instance getter
    * @param Element tooltip owner
    * @param string | Element tooltip content
    * @param object options; 2nd and 3rd parameters order may be reversed
    */
   instance: function () {
      let element = arguments[0],
         param = ['string', 'element'].contains(typeOf(arguments[1])),
         content = param ? arguments[1] : arguments[2] || null,
         options = param ? arguments[2] || {} : arguments[1];
      let current, tooltip;

      if (typeOf((current = document.retrieve('Tooltip.current'))) == 'object') {
         current.hide();
      }

      if ((tooltip = element.retrieve('Tooltip.instance')) == null) {
         tooltip = new Tooltip(element, options);
      } else {
         tooltip.setOptions(options);
      }

      element.store('Tooltip.instance', tooltip);
      document.store('Tooltip.current', tooltip);

      if (content) {
         tooltip.set(content);
      }

      return tooltip;
   },
});
